import requests from "./httpServices.js";

class FolderService {
  /**
   * Create a new folder
   * @param data - Folder creation data
   * @returns Promise<FolderResponse>
   */
  async createFolder(data) {
    try {
      const response = await requests.post(`/element/create-folder`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all folders with optional pagination and filters
   * @param params - Query parameters for filtering and pagination
   * @returns Promise<FoldersResponse>
   */
  async getFolders() {
    try {
      const url = `/element/get-folders`;
      const response = await requests.get(url);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a single folder by ID
   * @param folderId - Folder ID
   * @returns Promise<FolderResponse>
   */
  async getFolderById(folderId) {
    try {
      const response = await requests.get(`/element/get-folders/${folderId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCtaByFolderId(folderId) {
    try {
      const response = await requests.get(`/element/cta/${folderId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a folder
   * @param folderId - Folder ID
   * @param data - Folder update data
   * @returns Promise<FolderResponse>
   */
  async updateFolder(folderId, data) {
    try {
      const response = await requests.put(`/element/folder/${folderId}`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a folder
   * @param folderId - Folder ID
   * @returns Promise<DeleteFolderResponse>
   */
  async deleteFolder(folderId) {
    try {
      const response = await requests.delete(
        `/element/delete-folder/${folderId}`
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get root folders (folders without parent)
   * @returns Promise<FoldersResponse>
   */
  async getRootFolders() {
    try {
      const response = await requests.get(`/element/folders?parentId=null`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPresetsByRoomId(roomId) {
    try {
      const response = await requests.get(
        `/element/presets-by-room-id/${roomId}`
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new element in a folder
   * @param folderId - Folder ID
   * @param data - Element data
   * @returns Promise<ElementResponse>
   */
  async createElement(folderId, data) {
    try {
      const { name, type, folder_id, folderId: _fid, ...rest } = data;
      // Strip DB metadata that shouldn't be in settings
      // Keep `settings` only when it's a live object (e.g. prompter config);
      // discard it when it's a string (already-serialized DB artifact)
      const { _id, __v, id, createdAt, updatedAt, userId, displayStyle, settings, pollId, is_favorite, sortorder, ...cleanRest } = rest;
      const settingsPayload = (settings && typeof settings === 'object')
        ? { ...cleanRest, settings }
        : cleanRest;
      const response = await requests.post(`/element/create-element`, {
        name,
        type,
        folderId: folderId || _fid || folder_id,
        settings: JSON.stringify(settingsPayload),
        ...(pollId ? { pollId } : {}),
      });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an element
   * @param elementId - Element ID
   * @param data - Element update data
   * @returns Promise<ElementResponse>
   */
  async updateElement(elementId, data) {
    try {
      const { name, type, folderId, folder_id, ...rest } = data;
      // Strip DB metadata that shouldn't be in settings
      // Keep `settings` only when it's a live object (e.g. prompter config);
      // discard it when it's a string (already-serialized DB artifact)
      const { _id, __v, id, createdAt, updatedAt, userId, displayStyle, settings, pollId, is_favorite, sortorder, ...cleanRest } = rest;
      const settingsPayload = (settings && typeof settings === 'object')
        ? { ...cleanRest, settings }
        : cleanRest;
      const response = await requests.post(`/element/update-element`, {
        id: elementId,
        name,
        folderId: folderId || folder_id,
        settings: JSON.stringify(settingsPayload),
        ...(pollId ? { pollId } : {}),
      });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete an element
   * @param elementId - Element ID
   * @returns Promise<DeleteResponse>
   */
  async deleteElement(elementId) {
    try {
      const response = await requests.post(`/element/delete`, { id: elementId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Move an element to a different folder
   * @param elementId - Element ID
   * @param newFolderId - New folder ID
   * @returns Promise<ElementResponse>
   */
  async moveElement(elementId, newFolderId) {
    try {
      const response = await requests.post(`/element/update-element`, {
        id: elementId,
        folderId: newFolderId,
      });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reorder elements within a folder
   * @param elements - Array of { id, order } objects
   * @returns Promise<Response>
   */
  async reorderElements(elements) {
    try {
      const response = await requests.put(`/element/reorder`, { elements });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reorder folders
   * @param folders - Array of { id, order } objects
   * @returns Promise<Response>
   */
  async reorderFolders(folders) {
    try {
      const response = await requests.put(`/element/reorder-folders`, { folders });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate (or return existing) share code for an element the user owns
   */
  async generateShareCode(elementId) {
    try {
      const response = await requests.post(`/element/share-code`, { elementId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove share code so the element is no longer importable
   */
  async unshareElement(elementId) {
    try {
      const response = await requests.post(`/element/unshare`, { elementId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Look up element metadata by share code (preview before copy)
   */
  async getElementByShareCode(code) {
    try {
      const response = await requests.get(`/element/shared-code/${encodeURIComponent(code)}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Copy an element into a chosen folder using its share code
   */
  async copyElementByShareCode(code, folderId) {
    try {
      const response = await requests.post(`/element/copy-by-code`, { code, folderId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate (or return existing) share code for a folder the user owns.
   * The same code unlocks copying the folder + every element inside it.
   */
  async generateFolderShareCode(folderId) {
    try {
      const response = await requests.post(`/element/folder/share-code`, { folderId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Revoke a folder's share code so existing in-the-wild codes stop working.
   */
  async unshareFolder(folderId) {
    try {
      const response = await requests.post(`/element/folder/unshare`, { folderId });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Look up folder metadata (name + element count) by share code so the UI
   * can preview before importing. Returns null on 404 so callers can fall
   * back to the element-share lookup without throwing.
   */
  async getFolderByShareCode(code) {
    try {
      const response = await requests.get(`/element/folder/shared-code/${encodeURIComponent(code)}`);
      return response;
    } catch (error) {
      if (error?.response?.status === 404) return null;
      throw this.handleError(error);
    }
  }

  /**
   * Clone a shared folder + all elements into the requesting user's account.
   * Returns the new folder object the server created.
   */
  async copyFolderByShareCode(code) {
    try {
      const response = await requests.post(`/element/folder/copy-by-code`, { code });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param error - Error object
   * @returns Error with formatted message
   */
  handleError(error) {
    if (error.response?.data) {
      const { message, errors } = error.response.data;

      if (errors && typeof errors === "object") {
        // Handle validation errors
        const errorMessages = Object.values(errors).flat();
        return new Error(errorMessages.join(", "));
      }

      if (message) {
        return new Error(message);
      }
    }

    if (error.message) {
      return new Error(error.message);
    }

    return new Error("An unexpected error occurred");
  }
}

// Export singleton instance
const folderService = new FolderService();
export default folderService;
